/**
 * MitigationVisual — 2D structure render with atom highlights.
 *
 * Theme A (Bill Kinney walkthrough 2026-05-14): when Gibbs surfaces a
 * mitigation suggestion targeting a specific position on a compound
 * (e.g., "add ortho substituent to the aniline"), show the compound's
 * 2D structure with the affected atoms visually highlighted alongside
 * the prose suggestion.
 *
 * Uses RDKit-WASM's native `get_svg_with_highlights` to generate the
 * SVG inline — no canvas overlays, no coordinate-system adapters. The
 * resolver (`mitigationHighlightResolver.js`) supplies the atom indices.
 *
 * Renders progressively:
 *   - While RDKit is loading: the prose caption alone (no flicker).
 *   - If RDKit fails to load (offline, WASM blocked): the prose caption
 *     alone with a small note that the visual is unavailable.
 *   - On success: SVG inline with caption underneath. Accessible alt
 *     description via aria-label so screen-reader users get the prose
 *     fallback automatically.
 *
 * @module ui/MitigationVisual
 */

import React, { useEffect, useState } from 'react';

const DEFAULT_WIDTH = 280;
const DEFAULT_HEIGHT = 180;
const HIGHLIGHT_COLOUR_DEFAULT = [0.95, 0.2, 0.2];

/**
 * @param {Object} props
 * @param {string} props.smiles - The compound SMILES
 * @param {number[]} [props.atomIndices=[]] - Atom indices to highlight
 * @param {string} [props.caption] - Prose annotation under the visual
 * @param {number[]} [props.colour] - RGB triple (0..1) for the highlight
 * @param {number} [props.width=280]
 * @param {number} [props.height=180]
 */
export function MitigationVisual({
    smiles,
    atomIndices = [],
    caption,
    colour = HIGHLIGHT_COLOUR_DEFAULT,
    width = DEFAULT_WIDTH,
    height = DEFAULT_HEIGHT,
}) {
    const [svg, setSvg] = useState(null);
    const [status, setStatus] = useState('loading');

    useEffect(() => {
        let cancelled = false;

        async function renderSvg() {
            if (!smiles) {
                if (!cancelled) setStatus('no-smiles');
                return;
            }
            try {
                const { loadRDKit } = await import(
                    '../gibbs/knowledge/rdkitLoader'
                );
                const RDKit = await loadRDKit();
                if (cancelled) return;
                if (!RDKit.isInitialized || !RDKit.isInitialized()) {
                    setStatus('rdkit-unavailable');
                    return;
                }

                const mol = RDKit.getMol(smiles);
                if (!mol || !mol.is_valid()) {
                    if (mol && mol.delete) mol.delete();
                    if (!cancelled) setStatus('invalid-smiles');
                    return;
                }

                try {
                    const details = {
                        width,
                        height,
                        atoms: atomIndices,
                        highlightAtomColors: atomIndices.reduce(
                            (acc, idx) => { acc[idx] = colour; return acc; },
                            {}
                        ),
                    };
                    const svgString = mol.get_svg_with_highlights(
                        JSON.stringify(details)
                    );
                    if (!cancelled) {
                        setSvg(svgString);
                        setStatus('ready');
                    }
                } finally {
                    if (mol.delete) mol.delete();
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('[MitigationVisual] render error:', err?.message);
                if (!cancelled) setStatus('rdkit-unavailable');
            }
        }

        renderSvg();
        return () => { cancelled = true; };
    }, [smiles, JSON.stringify(atomIndices), JSON.stringify(colour), width, height]);

    // Accessible alt-text: screen-reader users get the prose fallback.
    // Theme A acceptance criterion #6.
    const altText = caption
        ? `2D structure with ${atomIndices.length} atom(s) highlighted: ${caption}`
        : '2D structure visual';

    // SVG inserted via dangerouslySetInnerHTML so React mounts the
    // RDKit-generated markup. The string is generated client-side from
    // RDKit-WASM and never originates from user input that could carry
    // malicious markup.
    return (
        <div
            className="gibbs-mitigation-visual"
            role="figure"
            aria-label={altText}
            style={{
                margin: '8px 0',
                padding: '4px',
                border: '1px solid rgba(127,127,127,0.25)',
                borderRadius: '4px',
            }}
        >
            {status === 'ready' && svg && (
                // eslint-disable-next-line react/no-danger
                <div
                    style={{ display: 'flex', justifyContent: 'center' }}
                    dangerouslySetInnerHTML={{ __html: svg }}
                />
            )}
            {status === 'loading' && (
                <div style={{ height, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', opacity: 0.6, fontSize: '0.85em' }}>
                    Rendering structure…
                </div>
            )}
            {(status === 'rdkit-unavailable'
                || status === 'invalid-smiles'
                || status === 'no-smiles') && (
                <div style={{ padding: '8px', opacity: 0.7, fontSize: '0.85em' }}>
                    (2D structure unavailable — see prose below.)
                </div>
            )}
            {caption && (
                <div style={{
                    textAlign: 'center',
                    fontSize: '0.85em',
                    opacity: 0.85,
                    marginTop: '4px',
                }}>
                    {caption}
                </div>
            )}
        </div>
    );
}

export default MitigationVisual;
